import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/DashboardSidebar';
import { convertHeicFilesToJpeg } from '../lib/heicConvert';
import './AddProduct.css'; // Reuse same styles

const API_URL = process.env.REACT_APP_API_URL || 'https://orders.fletaime.com';

const CATEGORIES = [
  { value: 'travel', label: 'Travel Book' },
  { value: 'wedding', label: 'Love Book' },
  { value: 'baby', label: 'Baby Book' },
  { value: 'agenda', label: 'Agjenda 2026' },
  { value: 'kitchen', label: 'Ditari i Kuzhinës' },
  { value: 'kids', label: 'Kids Book' },
  { value: 'frame', label: 'Kornizë Muri' },
];

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({
    name: '',
    name_en: '',
    name_de: '',
    category: 'travel',
    short_description: '',
    short_description_en: '',
    short_description_de: '',
    full_description: '',
    full_description_en: '',
    full_description_de: '',
    price: '',
    original_price: '',
    is_bestseller: false,
  });

  const [showTranslations, setShowTranslations] = useState(false);
  // Existing images (URLs already saved)
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  // New images to upload (File objects + preview URLs)
  const [newImages, setNewImages] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [features, setFeatures] = useState(['']);
  const [featuresEn, setFeaturesEn] = useState(['']);
  const [featuresDe, setFeaturesDe] = useState(['']);

  useEffect(() => {
    if (localStorage.getItem('dashboardAuth') !== 'true') {
      navigate('/dashboard/sign-in');
      return;
    }
    fetch(`${API_URL}/api/products/${id}`)
      .then(r => r.json())
      .then(p => {
        if (p.error) { setNotFound(true); setLoading(false); return; }
        setForm({
          name: p.name || '',
          name_en: p.name_en || '',
          name_de: p.name_de || '',
          category: p.category || 'travel',
          short_description: p.short_description || '',
          short_description_en: p.short_description_en || '',
          short_description_de: p.short_description_de || '',
          full_description: p.full_description || '',
          full_description_en: p.full_description_en || '',
          full_description_de: p.full_description_de || '',
          price: p.price ?? '',
          original_price: p.original_price ?? '',
          is_bestseller: p.is_bestseller || false,
        });
        setExistingImageUrls(p.image_urls || []);
        setFeatures(p.features && p.features.length > 0 ? p.features : ['']);
        setFeaturesEn(p.features_en && p.features_en.length > 0 ? p.features_en : ['']);
        setFeaturesDe(p.features_de && p.features_de.length > 0 ? p.features_de : ['']);
        setShowTranslations(Boolean(p.name_en || p.name_de || p.short_description_en || p.short_description_de));
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id, navigate]);

  const handleChange = e => {
    const { name, type, value, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFeatureChange = (i, val) => {
    setFeatures(prev => { const arr = [...prev]; arr[i] = val; return arr; });
  };
  const addFeature = () => setFeatures(prev => [...prev, '']);
  const removeFeature = i => setFeatures(prev => prev.filter((_, idx) => idx !== i));

  const handleFeatureEnChange = (i, val) => {
    setFeaturesEn(prev => { const arr = [...prev]; arr[i] = val; return arr; });
  };
  const addFeatureEn = () => setFeaturesEn(prev => [...prev, '']);
  const removeFeatureEn = i => setFeaturesEn(prev => prev.filter((_, idx) => idx !== i));

  const handleFeatureDeChange = (i, val) => {
    setFeaturesDe(prev => { const arr = [...prev]; arr[i] = val; return arr; });
  };
  const addFeatureDe = () => setFeaturesDe(prev => [...prev, '']);
  const removeFeatureDe = i => setFeaturesDe(prev => prev.filter((_, idx) => idx !== i));

  const removeExistingImage = i => {
    setExistingImageUrls(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleNewImages = async e => {
    const files = await convertHeicFilesToJpeg(Array.from(e.target.files));
    setNewImages(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setNewPreviews(prev => [...prev, ...previews]);
    e.target.value = '';
  };

  const removeNewImage = i => {
    URL.revokeObjectURL(newPreviews[i]);
    setNewImages(prev => prev.filter((_, idx) => idx !== i));
    setNewPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Emri është i detyrueshëm.'); return; }
    if (!form.price) { setError('Çmimi është i detyrueshëm.'); return; }

    setSaving(true);
    setError('');

    const fd = new FormData();
    fd.append('name', form.name.trim());
    fd.append('name_en', form.name_en.trim());
    fd.append('name_de', form.name_de.trim());
    fd.append('category', form.category);
    fd.append('short_description', form.short_description.trim());
    fd.append('short_description_en', form.short_description_en.trim());
    fd.append('short_description_de', form.short_description_de.trim());
    fd.append('full_description', form.full_description.trim());
    fd.append('full_description_en', form.full_description_en.trim());
    fd.append('full_description_de', form.full_description_de.trim());
    fd.append('price', form.price);
    fd.append('original_price', form.original_price);
    fd.append('is_bestseller', form.is_bestseller);
    fd.append('features', JSON.stringify(features.filter(f => f.trim())));
    fd.append('features_en', JSON.stringify(featuresEn.filter(f => f.trim())));
    fd.append('features_de', JSON.stringify(featuresDe.filter(f => f.trim())));
    fd.append('existing_image_urls', JSON.stringify(existingImageUrls));
    newImages.forEach(img => fd.append('images', img));

    try {
      const res = await fetch(`${API_URL}/api/products/${id}`, { method: 'PUT', body: fd });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Gabim gjatë ruajtjes.'); setSaving(false); return; }
      navigate('/dashboard/products');
    } catch {
      setError('Gabim gjatë lidhjes me serverin.');
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content"><div className="pa-loading">Duke ngarkuar...</div></div>
    </div>
  );

  if (notFound) return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        <div className="ap-header">
          <button className="ap-back" onClick={() => navigate('/dashboard/products')}>← Kthehu</button>
          <h2>Produkti nuk u gjet</h2>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        <div className="ap-header">
          <button className="ap-back" onClick={() => navigate('/dashboard/products')}>← Kthehu</button>
          <h2>Ndrysho Produktin</h2>
        </div>

        <form className="ap-form" onSubmit={handleSubmit}>
          {error && <div className="ap-error">{error}</div>}

          <div className="ap-row">
            <div className="ap-field">
              <label>Emri i Produktit *</label>
              <input name="name" value={form.name} onChange={handleChange} placeholder="p.sh. Travel Book - Paris" />
            </div>
            <div className="ap-field">
              <label>Kategoria *</label>
              <select name="category" value={form.category} onChange={handleChange}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="ap-field">
            <label>Përshkrimi i shkurtër</label>
            <input name="short_description" value={form.short_description} onChange={handleChange} placeholder="Një fjali e shkurtër për produktin" />
          </div>

          <div className="ap-field">
            <label>Përshkrimi i plotë</label>
            <textarea name="full_description" value={form.full_description} onChange={handleChange} rows={4} placeholder="Përshkrimi i detajuar i produktit..." />
          </div>

          <div className="ap-row">
            <div className="ap-field">
              <label>Çmimi (€) *</label>
              <input name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleChange} placeholder="21.25" />
            </div>
            <div className="ap-field">
              <label>Çmimi origjinal (€) – para zbritjes</label>
              <input name="original_price" type="number" step="0.01" min="0" value={form.original_price} onChange={handleChange} placeholder="24.99" />
            </div>
          </div>

          <div className="ap-field ap-checkbox-field">
            <label>
              <input name="is_bestseller" type="checkbox" checked={form.is_bestseller} onChange={handleChange} />
              {' '}Shfaqe si "Më të Shitura" në ballinë (opsionale)
            </label>
          </div>

          <div className="ap-section">
            <label>Karakteristikat</label>
            {features.map((f, i) => (
              <div className="ap-feature-row" key={i}>
                <input
                  value={f}
                  onChange={e => handleFeatureChange(i, e.target.value)}
                  placeholder={`Karakteristika ${i + 1}`}
                />
                {features.length > 1 && (
                  <button type="button" className="ap-feature-remove" onClick={() => removeFeature(i)}>✕</button>
                )}
              </div>
            ))}
            <button type="button" className="ap-feature-add" onClick={addFeature}>+ Shto karakteristikë</button>
          </div>

          <div className="ap-section">
            <button type="button" className="ap-feature-add" onClick={() => setShowTranslations(v => !v)}>
              {showTranslations ? '− Fshih përkthimet EN / DE' : '+ Shto përkthime EN / DE (opsionale)'}
            </button>
            {!showTranslations && (
              <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>
                Nëse i lë bosh, sajti shfaq automatikisht shqipen për këto gjuhë.
              </div>
            )}

            {showTranslations && (
              <>
                <div className="ap-field" style={{ marginTop: 12 }}>
                  <label>Emri (EN)</label>
                  <input name="name_en" value={form.name_en} onChange={handleChange} placeholder="e.g. Travel Book - Paris" />
                </div>
                <div className="ap-field">
                  <label>Përshkrimi i shkurtër (EN)</label>
                  <input name="short_description_en" value={form.short_description_en} onChange={handleChange} placeholder="A short sentence about the product" />
                </div>
                <div className="ap-field">
                  <label>Përshkrimi i plotë (EN)</label>
                  <textarea name="full_description_en" value={form.full_description_en} onChange={handleChange} rows={3} placeholder="Detailed product description..." />
                </div>
                <div className="ap-field">
                  <label>Karakteristikat (EN)</label>
                  {featuresEn.map((f, i) => (
                    <div className="ap-feature-row" key={i}>
                      <input value={f} onChange={e => handleFeatureEnChange(i, e.target.value)} placeholder={`Feature ${i + 1}`} />
                      {featuresEn.length > 1 && (
                        <button type="button" className="ap-feature-remove" onClick={() => removeFeatureEn(i)}>✕</button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="ap-feature-add" onClick={addFeatureEn}>+ Add feature</button>
                </div>

                <div className="ap-field" style={{ marginTop: 12 }}>
                  <label>Emri (DE)</label>
                  <input name="name_de" value={form.name_de} onChange={handleChange} placeholder="z.B. Travel Book - Paris" />
                </div>
                <div className="ap-field">
                  <label>Përshkrimi i shkurtër (DE)</label>
                  <input name="short_description_de" value={form.short_description_de} onChange={handleChange} placeholder="Ein kurzer Satz über das Produkt" />
                </div>
                <div className="ap-field">
                  <label>Përshkrimi i plotë (DE)</label>
                  <textarea name="full_description_de" value={form.full_description_de} onChange={handleChange} rows={3} placeholder="Detaillierte Produktbeschreibung..." />
                </div>
                <div className="ap-field">
                  <label>Karakteristikat (DE)</label>
                  {featuresDe.map((f, i) => (
                    <div className="ap-feature-row" key={i}>
                      <input value={f} onChange={e => handleFeatureDeChange(i, e.target.value)} placeholder={`Merkmal ${i + 1}`} />
                      {featuresDe.length > 1 && (
                        <button type="button" className="ap-feature-remove" onClick={() => removeFeatureDe(i)}>✕</button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="ap-feature-add" onClick={addFeatureDe}>+ Merkmal hinzufügen</button>
                </div>
              </>
            )}
          </div>

          <div className="ap-section">
            <label>Fotot e Produktit</label>

            {existingImageUrls.length > 0 && (
              <>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Fotot aktuale (kliko ✕ për të hequr):</div>
                <div className="ap-previews">
                  {existingImageUrls.map((url, i) => (
                    <div className="ap-preview-item" key={url}>
                      <img src={url} alt={`foto ${i + 1}`} />
                      <button type="button" className="ap-preview-remove" onClick={() => removeExistingImage(i)}>✕</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="ap-image-upload-area" onClick={() => document.getElementById('ep-file-input').click()}>
              <div className="ap-upload-icon">📷</div>
              <div>Kliko për të shtuar foto të reja (ose një ZIP me shumë foto)</div>
              <div className="ap-upload-hint">JPG, PNG, WEBP – max 20MB secila, ose një .zip me to të gjitha</div>
            </div>
            <input
              id="ep-file-input"
              type="file"
              accept="image/*,.zip,application/zip"
              multiple
              style={{ display: 'none' }}
              onChange={handleNewImages}
            />
            {newPreviews.length > 0 && (
              <div className="ap-previews">
                {newPreviews.map((src, i) => {
                  const isZip = /\.zip$/i.test(newImages[i]?.name || '');
                  return (
                    <div className="ap-preview-item" key={i}>
                      {isZip ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '13px', textAlign: 'center', padding: '4px' }}>
                          <div style={{ fontSize: '28px' }}>📦</div>
                          <div>{newImages[i]?.name}</div>
                          <div style={{ opacity: 0.7 }}>fotot nxirren automatikisht</div>
                        </div>
                      ) : (
                        <img src={src} alt={`new foto ${i + 1}`} />
                      )}
                      <button type="button" className="ap-preview-remove" onClick={() => removeNewImage(i)}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="ap-actions">
            <button type="button" className="ap-btn-cancel" onClick={() => navigate('/dashboard/products')}>
              Anulo
            </button>
            <button type="submit" className="ap-btn-save" disabled={saving}>
              {saving ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
